/**
 * The files attached in the composer, before they're sent (TMMAPP-0142 item 25a; TMMAPI-0181 U7).
 *
 * - **Attach:** each picked, dropped or pasted file is checked against the browser-side limits
 *   (`attachmentRules`), then uploads at once (`POST …/attachments`, multipart), with its progress.
 *   A refused file is listed with its reason and never uploads.
 * - **Remove:** an uploaded file is deleted (`DELETE …/attachments/{id}`); one still uploading is
 *   dropped now and deleted when its upload lands.
 * - **Send:** the uploads' ids go on the message. Send is held while any file isn't ready (uploading,
 *   failed, expired), so nothing is left behind unseen. An upload is swept by the server at its
 *   served `expiresAt` (24 h after the upload, J5): past it the chip shows as expired, with re-attach
 *   (the file is still held here, in memory). A send the server refuses for files it no longer has
 *   (`404 ASSISTANT_ATTACHMENT_NOT_FOUND`) marks only those (`details.missing`) expired, or every
 *   ready chip when it names none. Once the message is accepted only the files it carried leave the
 *   composer (`clearSent(ids)`, K2).
 * - **Limits** count every chip, a failed or expired one included, so re-attaching one never takes
 *   the message past five files (K4). The session's 50 MB is the server's to check
 *   (`413 … SESSION_BYTES`): with the transcript paged (TMMAPP-0148 U2), the browser no longer
 *   holds every message's files to sum.
 *
 * Files, previews and ids live in memory only — never in browser storage. An image's thumbnail is an
 * object URL of the picked file, revoked when its chip goes or the composer unmounts.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  assistantApi,
  type AssistantAttachment,
  type AssistantAttachmentKind,
} from '@/api/assistant';
import {
  ATTACHMENT_FILE_MAX_BYTES,
  ATTACHMENT_MESSAGE_MAX_FILES,
  fileKind,
  isBankFile,
} from './attachmentRules';

export type PendingStatus = 'uploading' | 'ready' | 'failed' | 'expired';

export interface PendingAttachment {
  /** This composer's own id for the chip. */
  key: number;
  file: File;
  /** What the file looks like it is (its type, else its name). */
  kind: AssistantAttachmentKind;
  status: PendingStatus;
  /** 0 to 100 while uploading; null until the browser reports. */
  progress: number | null;
  /** The server's answer once uploaded (`sentAsIs`, pages, rows). */
  attachment: AssistantAttachment | null;
  /** A failed upload's error. */
  error: unknown;
  /** An image's thumbnail (an object URL of the file); null otherwise. */
  previewUrl: string | null;
}

export type AttachmentRefusalReason = 'type' | 'file' | 'message';

export interface AttachmentRefusal {
  name: string;
  reason: AttachmentRefusalReason;
}

export interface AttachmentsController {
  items: readonly PendingAttachment[];
  /** Files the last attach refused in the browser, with why. */
  refusals: readonly AttachmentRefusal[];
  dismissRefusals: () => void;
  /** A bank export (CSV, OFX, QFX) was attached or tried: the composer points to Import. */
  bankFile: boolean;
  /** Sending waits: a file is uploading, failed or expired. */
  held: boolean;
  add: (files: readonly File[]) => void;
  remove: (key: number) => void;
  /** Uploads a failed or expired file again. */
  reattach: (key: number) => void;
  /**
   * The ids to send, or null when an upload is past its served `expiresAt` (its chip now says so,
   * and the message waits).
   */
  prepareSend: () => number[] | null;
  /**
   * The server no longer has the uploads (`404 ASSISTANT_ATTACHMENT_NOT_FOUND`): those named in
   * `missing` are expired, or every ready one when it names none.
   */
  markExpired: (missing?: readonly number[] | null) => void;
  /** The message was accepted: the files it carried (these ids) leave the composer. */
  clearSent: (ids: readonly number[]) => void;
}

interface AttachmentsOptions {
  sessionId: number;
}

function previewOf(file: File, kind: AssistantAttachmentKind): string | null {
  if (kind !== 'IMAGE' || typeof URL.createObjectURL !== 'function') return null;
  return URL.createObjectURL(file);
}

/** An upload the server has swept by now: past its served `expiresAt`. */
function pastExpiry(item: PendingAttachment, now: number): boolean {
  const expiresAt = item.attachment?.expiresAt ?? null;
  if (item.status !== 'ready' || expiresAt === null) return false;
  const at = Date.parse(expiresAt);
  return Number.isFinite(at) && now >= at;
}

function revoke(item: PendingAttachment): void {
  if (item.previewUrl !== null && typeof URL.revokeObjectURL === 'function') {
    URL.revokeObjectURL(item.previewUrl);
  }
}

export function useAttachments({ sessionId }: AttachmentsOptions): AttachmentsController {
  const [items, setItems] = useState<PendingAttachment[]>([]);
  const [refusals, setRefusals] = useState<AttachmentRefusal[]>([]);
  // The source of truth for reads outside a render (an upload landing, a send).
  const itemsRef = useRef<PendingAttachment[]>([]);
  const nextKey = useRef(1);
  // Chips removed while their upload ran: the upload is deleted when it lands.
  const dropped = useRef(new Set<number>());
  const mounted = useRef(true);

  const update = useCallback((next: (current: PendingAttachment[]) => PendingAttachment[]) => {
    itemsRef.current = next(itemsRef.current);
    setItems(itemsRef.current);
  }, []);

  const patch = useCallback(
    (key: number, changes: Partial<PendingAttachment>) =>
      update((current) =>
        current.map((item) => (item.key === key ? { ...item, ...changes } : item)),
      ),
    [update],
  );

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      itemsRef.current.forEach(revoke);
    };
  }, []);

  const upload = useCallback(
    (key: number, file: File) => {
      assistantApi
        .uploadAttachment(sessionId, file, (event) => {
          if (!mounted.current || event.total === undefined || event.total <= 0) return;
          patch(key, { progress: Math.min(100, Math.round((event.loaded / event.total) * 100)) });
        })
        .then(
          (attachment) => {
            if (!mounted.current || dropped.current.has(key)) {
              dropped.current.delete(key);
              void assistantApi
                .deleteAttachment(sessionId, attachment.attachmentId)
                .catch(() => undefined);
              return;
            }
            patch(key, { status: 'ready', attachment, progress: 100, error: null });
          },
          (error: unknown) => {
            if (!mounted.current || dropped.current.has(key)) {
              dropped.current.delete(key);
              return;
            }
            patch(key, { status: 'failed', error, progress: null });
          },
        );
    },
    [sessionId, patch],
  );

  const add = useCallback(
    (files: readonly File[]) => {
      const refused: AttachmentRefusal[] = [];
      const accepted: PendingAttachment[] = [];
      // Every chip counts, a failed or expired one too: it may be re-attached (K4).
      const held = itemsRef.current;
      let count = held.length;
      for (const file of files) {
        const kind = fileKind(file);
        const reason: AttachmentRefusalReason | null =
          kind === null
            ? 'type'
            : file.size > ATTACHMENT_FILE_MAX_BYTES
              ? 'file'
              : count >= ATTACHMENT_MESSAGE_MAX_FILES
                ? 'message'
                : null;
        if (kind === null || reason !== null) {
          refused.push({ name: file.name, reason: reason ?? 'type' });
          continue;
        }
        count += 1;
        accepted.push({
          key: nextKey.current++,
          file,
          kind,
          status: 'uploading',
          progress: null,
          attachment: null,
          error: null,
          previewUrl: previewOf(file, kind),
        });
      }
      setRefusals(refused);
      if (accepted.length === 0) return;
      update((current) => [...current, ...accepted]);
      for (const item of accepted) upload(item.key, item.file);
    },
    [update, upload],
  );

  const remove = useCallback(
    (key: number) => {
      const item = itemsRef.current.find((candidate) => candidate.key === key);
      if (item === undefined) return;
      revoke(item);
      update((current) => current.filter((candidate) => candidate.key !== key));
      if (item.status === 'uploading') {
        dropped.current.add(key);
      } else if (item.status === 'ready' && item.attachment !== null) {
        // Gone from the composer either way; an upload the server keeps is swept within 24 h.
        void assistantApi
          .deleteAttachment(sessionId, item.attachment.attachmentId)
          .catch(() => undefined);
      }
    },
    [sessionId, update],
  );

  const reattach = useCallback(
    (key: number) => {
      const item = itemsRef.current.find((candidate) => candidate.key === key);
      if (item === undefined || item.status === 'uploading' || item.status === 'ready') return;
      patch(key, { status: 'uploading', progress: null, attachment: null, error: null });
      upload(key, item.file);
    },
    [patch, upload],
  );

  const prepareSend = useCallback(() => {
    const now = Date.now();
    if (itemsRef.current.some((item) => pastExpiry(item, now))) {
      update((current) =>
        current.map((item) =>
          pastExpiry(item, now) ? { ...item, status: 'expired' as const } : item,
        ),
      );
      return null;
    }
    return itemsRef.current.flatMap((item) =>
      item.status === 'ready' && item.attachment !== null ? [item.attachment.attachmentId] : [],
    );
  }, [update]);

  const markExpired = useCallback(
    (missing?: readonly number[] | null) => {
      const gone = missing === undefined || missing === null ? null : new Set(missing);
      update((current) =>
        current.map((item) =>
          item.status === 'ready' &&
          (gone === null || (item.attachment !== null && gone.has(item.attachment.attachmentId)))
            ? { ...item, status: 'expired' as const }
            : item,
        ),
      );
    },
    [update],
  );

  const clearSent = useCallback(
    (ids: readonly number[]) => {
      const sent = new Set(ids);
      const isSent = (item: PendingAttachment) =>
        item.attachment !== null && sent.has(item.attachment.attachmentId);
      itemsRef.current.filter(isSent).forEach(revoke);
      update((current) => current.filter((item) => !isSent(item)));
      setRefusals([]);
    },
    [update],
  );

  return {
    items,
    refusals,
    dismissRefusals: () => setRefusals([]),
    bankFile:
      items.some((item) => isBankFile(item.file.name)) ||
      refusals.some((refusal) => isBankFile(refusal.name)),
    held: items.some((item) => item.status !== 'ready'),
    add,
    remove,
    reattach,
    prepareSend,
    markExpired,
    clearSent,
  };
}
